import { useEffect } from 'react';

/**
 * Hook to manage automatic conversation selection behavior
 * 
 * Handles two scenarios:
 * 1. Auto-select first conversation on desktop when none is selected
 * 2. Clear selection when filters produce no results or selected conversation is filtered out
 * 
 * @param {Object} params - Configuration parameters
 * @param {boolean} params.isMobile - Whether the view is mobile
 * @param {string|null} params.selectedId - Currently selected conversation ID
 * @param {Array} params.sortedConversations - Array of filtered/sorted conversations
 * @param {Function} params.onSelectConversation - Callback to select a conversation
 * @param {boolean} params.loading - Whether conversations are loading
 * @param {boolean} params.loadingSearch - Whether search is in progress
 */
const useConversationAutoSelect = ({
    isMobile,
    selectedId,
    sortedConversations,
    onSelectConversation,
    loading,
    loadingSearch
}) => {
    // Auto-select first conversation on desktop when none is selected
    useEffect(() => {
        if (!isMobile && !selectedId && sortedConversations.length > 0) {
            onSelectConversation(sortedConversations[0]);
        }
    }, [isMobile, selectedId, sortedConversations, onSelectConversation]);

    // Clear selection if filtered out or no conversations remain
    useEffect(() => {
        // Don't clear selection while loading; we only want to react to "final" empty results.
        if (loading || loadingSearch) return;
        if (!selectedId) return;

        const hasAny = sortedConversations.length > 0;
        const selectedStillVisible = sortedConversations.some(c => c?.id === selectedId);

        if (!hasAny || !selectedStillVisible) {
            onSelectConversation(null);
        }
    }, [loading, loadingSearch, selectedId, sortedConversations, onSelectConversation]);
};

export default useConversationAutoSelect;
